import React, { useState, useEffect } from 'react';
import { normalizeUsername } from '../../services/usernameService';
import { TOP_NICHES } from '../../constants/niches';

/**
 * EditIdentityModal - Unified modal for editing all identity fields
 * Replaces scattered inline edit links with a single modal form
 */

function EditIdentityModal({
  isOpen,
  onClose,
  profile,
  onSave,
  userId,
}) {
  const [formData, setFormData] = useState({
    displayName: '',
    username: '',
    creatorTitle: '',
    email: '',
    niches: [], // Array of niche strings (max 3)
    location: '',
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  
  // Local state for "Add niche" dropdown
  const [isAddingNiche, setIsAddingNiche] = useState(false);
  const [pendingNiche, setPendingNiche] = useState('');
  const [customNiche, setCustomNiche] = useState('');

  // Initialize form data when modal opens or profile changes
  useEffect(() => {
    if (isOpen && profile) {
      const identity = profile.identity || {};
      
      // Get existing niches (handle both array and string formats, and legacy fields)
      let existingNiches = [];;
      if (Array.isArray(profile.niches)) {
        existingNiches = profile.niches.filter(Boolean);
      } else if (Array.isArray(profile.niche)) {
        existingNiches = profile.niche.filter(Boolean);
      } else if (identity.subtitle) {
        // Handle comma-separated string from identity.subtitle
        existingNiches = identity.subtitle.split(',').map(n => n.trim()).filter(Boolean);
      } else if (profile.niche) {
        // Handle single string niche
        existingNiches = [profile.niche];
      }
      
      setFormData({
        displayName: identity.displayName || profile.displayName || '',
        username: identity.handle || profile.username || '',
        creatorTitle: identity.creatorTitle || profile.creatorTitle || '',
        email: profile.contactEmail || profile.email || '',
        niches: existingNiches.slice(0, 3), // Max 3 niches
        location: identity.location || profile.location || '',
      });
      setErrors({});
      // Reset "Add niche" state when modal opens
      setIsAddingNiche(false);
      setPendingNiche('');
      setCustomNiche('');
    }
  }, [isOpen, profile]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    // Username is read-only, so no need to handle username field changes
  };

  const handleStartAddingNiche = () => {
    setIsAddingNiche(true);
    setPendingNiche('');
    setCustomNiche('');
  };

  const handleConfirmAddNiche = () => {
    let nicheToAdd = pendingNiche;

    if (pendingNiche === '__other') {
      nicheToAdd = customNiche.trim();
    }

    if (!nicheToAdd) return;

    const current = formData.niches || [];

    // Avoid duplicates
    if (current.includes(nicheToAdd)) {
      setIsAddingNiche(false);
      setPendingNiche('');
      setCustomNiche('');
      return;
    }

    if (current.length >= 3) {
      setIsAddingNiche(false);
      setPendingNiche('');
      setCustomNiche('');
      return;
    }

    setFormData(prev => ({
      ...prev,
      niches: [...current, nicheToAdd],
    }));

    setIsAddingNiche(false);
    setPendingNiche('');
    setCustomNiche('');
  };

  const handleRemoveNiche = (niche) => {
    setFormData(prev => ({
      ...prev,
      niches: (prev.niches || []).filter(n => n !== niche),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validation
    if (!formData.displayName.trim()) {
      setErrors(prev => ({ ...prev, displayName: 'Display name is required' }));
      return;
    }

    // Username is read-only, so we don't validate or update it here
    // Get the current username from profile (don't allow changes)
    const normalizedUsername = normalizeUsername(profile?.identity?.handle || profile?.username || formData.username);

    // Validate email if provided
    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
        return;
      }
    }

    // Validate creatorTitle length
    if (formData.creatorTitle.length > 100) {
      setErrors(prev => ({ ...prev, creatorTitle: 'Creator title must be 100 characters or less' }));
      return;
    }

    setIsSaving(true);

    try {
      // Username is read-only - don't update it here
      // Users must change username in Settings
      const normalizedUsername = normalizeUsername(profile?.identity?.handle || profile?.username || formData.username);

      // Get niches array (filter out empty values)
      const nichesToSave = (formData.niches || []).filter(n => n && n.trim());

      // Call onSave with all identity fields (excluding username - it's read-only)
      await onSave({
        displayName: formData.displayName.trim(),
        // Don't include handle/username - it's read-only and should be changed in Settings
        creatorTitle: formData.creatorTitle.trim() || null,
        contactEmail: formData.email.trim() || null,
        subtitle: nichesToSave.length > 0 ? nichesToSave.join(', ') : null, // Keep subtitle for backward compatibility
        niches: nichesToSave, // New niches array field
        location: formData.location.trim() || null,
      });

      onClose();
    } catch (error) {
      console.error('Error saving identity:', error);
      setErrors(prev => ({ ...prev, general: 'Failed to save. Please try again.' }));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div className="relative z-50 w-full max-w-2xl rounded-xl bg-white shadow-xl border border-slate-200 mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Edit Identity
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Update your profile information
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {errors.general}
              </div>
            )}

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={`w-full rounded-md border px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:border-transparent ${
                  errors.displayName
                    ? 'border-red-300 focus:ring-red-400'
                    : 'border-slate-300 focus:ring-slate-400'
                }`}
                value={formData.displayName}
                onChange={(e) => handleFieldChange('displayName', e.target.value)}
                placeholder="Your full name"
              />
              {errors.displayName && (
                <p className="mt-1 text-xs text-red-500">{errors.displayName}</p>
              )}
            </div>

            {/* Username - Read-only */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Username
              </label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm">@</span>
                <input
                  type="text"
                  className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600 cursor-not-allowed"
                  value={formData.username}
                  readOnly
                  disabled
                />
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Used in your profile URL: {typeof window !== "undefined" ? window.location.host : "influencehub.net"}/u/{formData.username || 'yourusername'}
              </p>
              <p className="mt-1 text-xs text-slate-400 italic">
                To change your username, please go to Settings.
              </p>
            </div>

            {/* Creator Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Creator Title
              </label>
              <input
                type="text"
                className={`w-full rounded-md border px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:border-transparent ${
                  errors.creatorTitle
                    ? 'border-red-300 focus:ring-red-400'
                    : 'border-slate-300 focus:ring-slate-400'
                }`}
                value={formData.creatorTitle}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 100) {
                    handleFieldChange('creatorTitle', value);
                  }
                }}
                placeholder="Lifestyle & travel creator"
                maxLength={100}
              />
              <p className="mt-1 text-xs text-slate-500">
                Short tagline under your name. Example: "Lifestyle & travel creator"
              </p>
              {formData.creatorTitle.length > 0 && (
                <p className="mt-0.5 text-xs text-slate-400">
                  {formData.creatorTitle.length}/100 characters
                </p>
              )}
              {errors.creatorTitle && (
                <p className="mt-1 text-xs text-red-500">{errors.creatorTitle}</p>
              )}
            </div>

            {/* Contact Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                className={`w-full rounded-md border px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:border-transparent ${
                  errors.email
                    ? 'border-red-300 focus:ring-red-400'
                    : 'border-slate-300 focus:ring-slate-400'
                }`}
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                placeholder="you@yourbusiness.com"
              />
              <p className="mt-1 text-xs text-slate-500">
                This email will be shown on your public profile so brands can contact you.
              </p>
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Niche */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">
                  Niche
                </label>
                <span className="text-xs text-slate-500">
                  {(formData.niches || []).length}/3 selected
                </span>
              </div>

              {/* Selected niche chips */}
              <div className="flex flex-wrap gap-2">
                {(formData.niches || []).length === 0 && (
                  <span className="text-xs text-slate-400 italic">
                    No niches added yet.
                  </span>
                )}

                {(formData.niches || []).map((niche) => (
                  <button
                    key={niche}
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    onClick={() => handleRemoveNiche(niche)}
                  >
                    <span>{niche}</span>
                    <span className="text-slate-400 hover:text-slate-600">×</span>
                  </button>
                ))}
              </div>

              {/* Add niche button */}
              {!isAddingNiche && (
                <button
                  type="button"
                  className="mt-2 inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  onClick={handleStartAddingNiche}
                  disabled={(formData.niches || []).length >= 3}
                >
                  + Add niche
                </button>
              )}

              {/* Add niche dropdown */}
              {isAddingNiche && (
                <div className="mt-2 space-y-2 p-3 border border-slate-200 rounded-md bg-slate-50">
                  <select
                    value={pendingNiche}
                    onChange={(e) => setPendingNiche(e.target.value)}
                    className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                  >
                    <option value="">Select a niche</option>
                    {TOP_NICHES.map((niche) => (
                      <option key={niche} value={niche}>
                        {niche}
                      </option>
                    ))}
                    <option value="__other">Other (type your own)</option>
                  </select>

                  {pendingNiche === '__other' && (
                    <input
                      type="text"
                      value={customNiche}
                      onChange={(e) => setCustomNiche(e.target.value)}
                      className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                      placeholder="Type your custom niche"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && customNiche.trim() && (formData.niches || []).length < 3) {
                          e.preventDefault();
                          handleConfirmAddNiche();
                        }
                      }}
                    />
                  )}

                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      className="text-xs font-medium text-slate-500 hover:text-slate-700"
                      onClick={() => {
                        setIsAddingNiche(false);
                        setPendingNiche('');
                        setCustomNiche('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      onClick={handleConfirmAddNiche}
                      disabled={
                        !pendingNiche ||
                        (pendingNiche === '__other' && !customNiche.trim()) ||
                        (formData.niches || []).length >= 3
                      }
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              <p className="mt-1 text-xs text-slate-500">
                Select up to 3 niches. You can also add a custom niche.
              </p>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Location
              </label>
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                value={formData.location}
                onChange={(e) => handleFieldChange('location', e.target.value)}
                placeholder="e.g., San Francisco, CA"
              />
            </div>

          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-semibold text-white bg-ihTeal hover:bg-ihTeal-dark rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditIdentityModal;

